import { Message, ValidationRule } from 'react-hook-form';

type RegisterOptions = Partial<{
	required: Message | ValidationRule<boolean>;
	min: ValidationRule<number | string>;
	max: ValidationRule<number | string>;
	maxLength: ValidationRule<number>;
	minLength: ValidationRule<number>;
	pattern: ValidationRule<RegExp>;
}>;

export const getValidator = (
	translation: any,
	maxLength: number | null,
	pattern: { value: RegExp; message: string } | null,
	required: { value: boolean; message?: string },
	minLength: number = 2,
	min?: number,
	max?: number | string
) => {
	const registerObject: RegisterOptions = {};

	if (maxLength)
		registerObject.maxLength = {
			value: maxLength,
			message:
				maxLength !== 64
					? translation('validators.maxLength_2', { count: maxLength })
					: translation('validators.maxLength_1', {
							count: maxLength,
						}),
		};

	if (minLength) {
		registerObject.minLength = {
			value: minLength,
			message:
				minLength < 5
					? translation('validators.minLength_1', { count: minLength })
					: translation('validators.minLength_2', {
							count: minLength,
						}),
		};
		if (required) {
			registerObject.required = {
				value: required.value,
				message: required.message ?? translation('validators.required'),
			};
		}
	}

	if (min) {
		registerObject.min = {
			value: min,
			message: translation('validators.minValue', { value: min }),
		};
	}

	if (max) {
		registerObject.max = {
			value: max,
			message: translation('validators.maxValue', { value: max }),
		};
	}

	if (pattern) registerObject.pattern = pattern;

	return registerObject;
};

type OverrideOptions = Partial<{
	maxLength: number | null;
	pattern: { value: RegExp; message: string } | null;
	required: { value: boolean; message?: string };
	minLength: number;
	min: number;
	max: number | string;
}>;

export const overrideValidator = (
	translation: any,
	validator: ReturnType<typeof getValidator>,
	override: OverrideOptions = {}
) => {
	const extractValue = (rule?: ValidationRule<number | string>) => {
		if (rule === null || rule === undefined) return null;
		if (typeof rule === 'object' && 'value' in rule) return rule.value;
		return rule;
	};

	const maxLength = override.maxLength ?? (extractValue(validator.maxLength) as number | null);
	const minLength = (override.minLength ?? (extractValue(validator.minLength) as number | undefined)) || undefined;
	const pattern = (override.pattern ?? (validator.pattern as { value: RegExp; message: string })) || null;
	const min = override.min ?? (extractValue(validator.min) as number | undefined);
	const max = (override.max ?? extractValue(validator.max)) || undefined;

	const requiredRule =
		override.required ??
		(typeof validator.required === 'object' && 'value' in validator.required
			? validator.required
			: { value: !!validator.required });

	const updatedValidator = getValidator(translation, maxLength, pattern, requiredRule, minLength, min, max);

	const standardKeys = new Set(['maxLength', 'pattern', 'required', 'minLength', 'min', 'max']);
	const mergedValidator: Record<string, unknown> = { ...updatedValidator };

	Object.keys(validator).forEach((key) => {
		if (!standardKeys.has(key)) {
			mergedValidator[key] = (validator as Record<string, unknown>)[key];
		}
	});

	return mergedValidator as typeof validator;
};
